import z from 'zod';

import { AdditionalVatSchema, validateVAT } from '@/components/VatSchema';
import { phoneNumberValidity, t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

const CompanySchema = () =>
    z
        .object({
            customerName: z.string().min(1, t('general.atLeastOneLetter')),
            companyRegistrationNumber: z.optional(z.string().regex(/^[0-9]*$/, t('general.onlyUseNumbers'))),
            address: z.string().min(1, t('general.atLeastOneLetter')),
            address2: z.optional(z.string()),
            postCode: z.string().min(1, t('general.atLeastOneLetterOrNumber')),
            city: z.string().min(1, t('general.atLeastOneLetter')),
            country: z.string().refine(country => country && country !== '', {
                message: t('general.selectOneCountry'),
            }),
        })
        .refine(
            company =>
                !(useUserStore.getState().isRegistrationNumberRequired && company.companyRegistrationNumber === ''),
            {
                message: t('general.atLeastOneNumber'),
                path: ['companyRegistrationNumber'],
            }
        );

const ContactSchema = () =>
    z
        .object({
            personalEmailAddress: z
                .string()
                .min(1, t('general.atLeastOneLetter'))
                .regex(/^[\w-.]+@([\w-]+\.)+[\w-]{2,10}$/, t('general.emailIsNotCorrect')),
            languageOfCommunication: z.string().refine(country => country && country !== '', {
                message: t('general.selectOneLanguage'),
            }),
            countryCode: z.string().min(1, t('general.atLeastOneLetter')),
            phoneNumber: z.string().regex(/^[0-9]*$/, t('general.onlyUseNumbers')),
        })
        .refine(invoice => phoneNumberValidity(invoice.countryCode, invoice.phoneNumber), {
            message: t('general.invalidPhoneNumber'),
            path: ['phoneNumber'],
        });

const InvoicingSchema = () => {
    const invalidVatMessage = t('general.invalidVAT');
    const eInvoicingDetails = useUserStore.getState().eInvoicingDetails ?? [];
    const hasEInvoicing = eInvoicingDetails.length > 0;
    const schemeMap = Object.fromEntries(
        eInvoicingDetails.map(({ scheme, regex }) => [
            scheme,
            { regex: new RegExp(regex), message: t(`onboarding.invoicingDetails.eInvoicing.invalidFormat.${scheme}`) },
        ])
    );

    return z
        .object({
            invoiceEmailAddress: z
                .string()
                .min(1, t('general.atLeastOneLetter'))
                .regex(/^[\w-.]+@([\w-]+\.)+[\w-]{2,10}$/, t('general.emailIsNotCorrect')),
            vat: z.string(),
            country: z.string(),
            additional: AdditionalVatSchema(),
            schemeId: hasEInvoicing
                ? z
                      .string()
                      .or(z.undefined())
                      .refine(val => val && val.length > 0, t('general.selectScheme'))
                : z.string().or(z.undefined()).optional(),
            participantIdentifier: hasEInvoicing
                ? z.string().min(1, t('general.atLeastOneLetter'))
                : z.string().optional(),
        })
        .superRefine((data, ctx) => {
            if (!validateVAT(data.vat, data.country)) {
                ctx.addIssue({
                    code: 'custom',
                    message: invalidVatMessage,
                    path: ['vat'],
                    input: data.vat,
                });
            }

            const scheme = data.schemeId ? schemeMap[data.schemeId] : undefined;
            if (scheme && !scheme.regex.test(data.participantIdentifier ?? '')) {
                ctx.addIssue({
                    code: 'custom',
                    message: scheme.message,
                    path: ['participantIdentifier'],
                });
            }
        });
};

export const InvoiceDataSchema = () =>
    z.object({
        company: CompanySchema(),
        contact: ContactSchema(),
        invoicing: InvoicingSchema(),
    });

export type InvoiceDataSchemaType = z.infer<ReturnType<typeof InvoiceDataSchema>>;
